import { useState, useEffect } from "react";
import {
  safeLocalStorageGet,
  safeLocalStorageSave,
} from "@/helpers/storageUtils";

const DETAILS_SIDEBAR_STATE_KEY = "details_sidebar_expanded";

/**
 * Custom hook to manage details sidebar expand/collapse state.
 * When isEditing is true, the sidebar is always expanded regardless of localStorage.
 * The expand/collapse state is shared across all page types.
 *
 * @param isEditing - Whether the page is in edit mode
 * @returns An object with isExpanded state and toggleSidebar function
 */
export const useDetailsSidebar = (isEditing: boolean) => {
  // Initialize from localStorage with default of true if not found
  const [isExpanded, setIsExpanded] = useState(() =>
    safeLocalStorageGet(DETAILS_SIDEBAR_STATE_KEY, true)
  );

  // When isEditing is true, always expand the sidebar (but don't save to localStorage)
  // When exiting edit mode, restore from localStorage
  useEffect(() => {
    if (!isEditing) {
      // Restore from localStorage when not editing
      const savedState = safeLocalStorageGet(DETAILS_SIDEBAR_STATE_KEY, true);
      setIsExpanded(savedState);
    }
  }, [isEditing]);

  const toggleSidebar = () => {
    // Don't allow toggling when in edit mode
    if (isEditing) {
      return;
    }

    const newState = !isExpanded;
    setIsExpanded(newState);
    safeLocalStorageSave(DETAILS_SIDEBAR_STATE_KEY, newState);
  };

  // When editing, always expanded; when not editing, use the actual state
  const effectiveIsExpanded = isEditing ? true : isExpanded;

  return {
    isExpanded: effectiveIsExpanded,
    toggleSidebar,
  };
};
